import React, { ReactElement, forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { AnyAction, Dispatch } from 'redux';
import { useDispatch } from 'react-redux';

import LoadingIndicator from "@Common/LoadingIndicator/LoadingIndicator";
import InputText from "@Common/InputText";
import Toggle from "@Common/Toggle/Toggle";
import MultiSelectBox from "@Common/MultiSelectBox/MultiSelectBox";
import Notification from '@Common/Notification';

import { actions as baselineActions } from '@Actions/baselineActions';
import * as types from '@Constants/actionTypes';
import { Option } from '@Types/common';
import { ThunkDispatch } from "redux-thunk";

const CreateBaseline = forwardRef((props: { subscriptionId: number, projectKey: string, onBaselineCreationComplete: () => void | null }, ref): ReactElement => {
    const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();


    const loadNewBaselineFilters = async (subscriptionId: number) => dispatch(baselineActions.loadFilters({ subscriptionId }));
    const saveBaseline = async (baseline: any) => dispatch(baselineActions.saveBaseline(baseline));

    const [isLoading, setIsLoading] = useState(false);
    const [newBaseline, setNewBaseline] = useState({
        id: 0,
        name: '',
        description: '',
        isActive: true,
        jobTitles: [] as number[],
        userIds: [] as number[],
        subscriptionId: props.subscriptionId,
        projectKey: props.projectKey
    });

    const [jobTitles, setJobTitles] = useState<Option[]>([]);
    const [users, setUsers] = useState<Option[]>([]);
    const [usersInModeratorRole, setUsersInModeratorRole] = useState<Option[]>([]);

    useEffect(() => {
        if (newBaseline.id === 0) {
            const nonModeratorUsers = users.filter(option => !usersInModeratorRole.some(item => item.id === option.id));

            setNewBaseline({
                ...newBaseline,
                jobTitles: jobTitles.map(option => option.id),
                userIds: nonModeratorUsers.map(option => option.id)
            });
        }
    }, [jobTitles, users, usersInModeratorRole])

    const loadFilters = async () => {
        setIsLoading(true);

        try {
            const result = await loadNewBaselineFilters(props.subscriptionId);
            const { payload } = result;

            if (result.type === types.LOAD_BASELINE_FILTERS_SUCCESS) {
                setJobTitles(payload.jobTitles);
                setUsers(payload.users);
                setUsersInModeratorRole(payload.usersInModeratorRole);
            } else {
                Notification("error", "An Error Occurred", payload.message)
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;

        loadFilters();

        return () => {
            isMounted = false;
        };
    }, [props.subscriptionId]);

    const createBaseline = async () => {
        setIsLoading(true);
        try {
            const result = await saveBaseline(newBaseline);
            if (result.type === types.CREATE_BASELINE_SUCCESS || result.type === types.UPDATE_BASELINE_SUCCESS) {
                Notification("success", "Save Successful", "Baseline saved successfully");
                props.onBaselineCreationComplete();
            } else {
                Notification("error", "An Error Occurred", result.payload.message);
                setIsLoading(false);
            }
        } catch (error) {
            Notification('error', 'Error', error);
            setIsLoading(false);
        }
    };

    useImperativeHandle(ref, () => ({
        createBaseline
    }));

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewBaseline({
            ...newBaseline,
            [e.target.name]: e.target.value
        });
    };

    const handleToggleChange = (name: string, value: string) => {
        setNewBaseline({
            ...newBaseline,
            [name]: value
        });
    }

    const handleDropdownChange = (value: string, name: string) => {
        setNewBaseline({
            ...newBaseline,
            [name]: value.split(',').map(Number)
        });
    }

    return <LoadingIndicator isLoading={isLoading} hideWhileLoading={true} isFullScreen={true}>
        <div className="container">
            <div className="row">
                <div className="col-8">
                    <div className="form-group">
                        <label htmlFor="name" className="title">What should this baseline be called?</label>
                        <InputText
                            id='name'
                            name='name'
                            placeholder='Name'
                            type='text'
                            value={newBaseline.name}
                            onChange={handleTextChange}
                            isRequired={true}
                            errorMessage='Task Name is required'
                            maxLength={50} />
                    </div>
                </div>
                <div className="col-4">
                    <div className="form-group">
                        <label htmlFor="isActive" className="title">Is Active?</label>
                        <Toggle
                            name='isActive'
                            text="Active"
                            checked={newBaseline.isActive}
                            onChange={handleToggleChange} />
                    </div>
                </div>
                <div className="col-12">
                    <div className="form-group">
                        <label htmlFor="description">Description</label>
                        <InputText
                            id='description'
                            name='description'
                            placeholder='Description'
                            type='text'
                            value={newBaseline.description}
                            onChange={handleTextChange}
                            isRequired={true}
                            errorMessage='Description is required'
                            maxLength={50} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="form-group">
                        <label htmlFor="jobTitles">What Job Titles should be able to access this baseline?</label>
                        <MultiSelectBox
                            name='jobTitles'
                            options={jobTitles}
                            idField='id'
                            valueField='name'
                            onChange={handleDropdownChange}
                            isRequired={false}
                            errorMessage='Job Titles are required'
                            isMulti={true}
                            placeholder='Search for Job Title to Add...'
                            selectedValues={newBaseline.jobTitles.join(',')} />
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-12">
                    <div className="form-group">
                        <label htmlFor="users">Who should be able to access this baseline?</label>
                        <MultiSelectBox
                            name='users'
                            options={users}
                            idField='id'
                            valueField='name'
                            onChange={handleDropdownChange}
                            isRequired={false}
                            errorMessage='Users are required'
                            isMulti={true}
                            placeholder='Search for Users to Add...'
                            selectedValues={newBaseline.userIds.join(',')} />
                    </div>
                </div>
            </div>
        </div>
    </LoadingIndicator>;
});

export default CreateBaseline;